import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getQuotesSummary } from '../../../shared/api/quotesSelection.api'
import { buildKpPrefillFromSelection } from '../utils/kpPrefillFromSelection'
import { toast } from '../../../shared/ui/toastStore'
import type { RequestItem } from '../../../shared/api/types'
import type { MarkupConstants } from '../../../shared/api/exchangeRates.api'
import type { SelectedPrice } from './useKpMode'
import type { PricingReadiness } from './useRequestRelatedData'

type KpModeApi = {
  enterKpMode: (pricingReady: boolean) => boolean
  setSelectedPrices: React.Dispatch<React.SetStateAction<Map<string, SelectedPrice>>>
}

/**
 * Вход в режим КП с карточки заявки.
 *
 * 1) Гейт готовности цен: без курса CNY и коэффициентов наценки режим не
 *    включается вовсе — иначе себестоимость считалась бы по фолбэкам и КП
 *    уходило клиенту заниженным (вплоть до 12,5 раза при курсе 1).
 * 2) Затем подтягиваем сводку «Выбор квот» и предзаполняем выбранные
 *    менеджером цены: ячейки отмеченных квот подсветятся автоматически.
 *    Если менеджер успел кликнуть ячейку до загрузки — его выбор не перетираем.
 */
export function useEnterKpMode(params: {
  requestId: string | undefined
  visibleItems: RequestItem[]
  cnyRate: number | null
  markupConstants: MarkupConstants | undefined
  pricing: PricingReadiness
  kp: KpModeApi
}) {
  const { requestId, visibleItems, cnyRate, markupConstants, pricing, kp } = params
  const queryClient = useQueryClient()

  return useCallback(() => {
    if (!kp.enterKpMode(pricing.ready) || cnyRate == null || !markupConstants) {
      toast.error('Курсы и коэффициенты не загрузились — КП посчитать нельзя')
      pricing.retry()
      return
    }
    if (!requestId) return
    void queryClient
      .fetchQuery({
        queryKey: ['quotes-summary', requestId],
        queryFn: () => getQuotesSummary(requestId),
      })
      .then((summary) => {
        const prefill = buildKpPrefillFromSelection({
          positions: summary.positions,
          items: visibleItems,
          cnyRate,
          markupConstants,
        })
        if (prefill.size === 0) return
        kp.setSelectedPrices((prev) => (prev.size > 0 ? prev : prefill))
      })
      .catch(() => {
        /* молча: если сводка не загрузилась, менеджер выберет цены вручную */
      })
  }, [requestId, queryClient, visibleItems, cnyRate, markupConstants, pricing, kp])
}
